import { FULL_MONTHS } from 'helpers/dateHelper';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    OperationType,
    ValueType,
    FreeFilters,
    FreeHypothesis,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { formatSimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/dataHelpers';
import { formatAmountsNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/dataHelpers';
import { t } from 'i18next';

const getFullMonth = (month: number): string => FULL_MONTHS[month];
export const getStartMonth = (startDate: SimulationDateString): string =>
    `${getFullMonth(new Date(startDate).getMonth())} ${new Date(
        startDate,
    ).getFullYear()}`;
export const getEndMonth = (endDate: SimulationDateString): string =>
    `${getFullMonth(new Date(endDate).getMonth())} ${new Date(
        endDate,
    ).getFullYear()}`;

function compareAlphabetically(a: string, b: string) {
    return a.localeCompare(b);
}

export const formatCategories = (categories: string[]): string[] => {
    // Custom comparison function to sort alphabetically
    // Sort the strings array in ascending order alphabetically
    const sortedStrings = categories.sort(compareAlphabetically);
    return sortedStrings;
};

export const formatOperationType = (
    operation: OperationType,
    value: number,
    valueType: ValueType,
): string => {
    const vType = valueType === ValueType.PERCENT ? '%' : '€';
    if (operation === OperationType.DECREASE) {
        return t(`humanResources.simulation.freeHypothesis.ifse.decreaseOf`, {
            value,
            type: vType,
        });
    }
    if (operation === OperationType.INCREASE)
        return t(`humanResources.simulation.freeHypothesis.ifse.increaseOf`, {
            value,
            type: vType,
        });
    return t(`humanResources.simulation.freeHypothesis.ifse.fixOf`, {
        value,
        type: vType,
    });
};
export const getHypothesisDetail = (hypothesis: FreeHypothesis): string => {
    return `${formatOperationType(hypothesis.operation, hypothesis.value, hypothesis.valueType)} le ${formatSimulationDateString(hypothesis.filters.date as SimulationDateString)}`;
};

const keyLabelFormatter = (key: string): string =>
    t(`humanResources.simulation.freeHypothesis.ifse.${key}`);
const jobLabelFormatter = (jobs: string[]): string =>
    `${jobs.map((item) => `${item},`).join(' ')}`;
export const formatHypothesisText = (
    category: string | undefined,
    filiere: string | undefined,
    cadreEmploi: string | undefined,
    job: string[] | undefined,
    applicationDate: SimulationDateString | undefined,
    minValue: number | undefined,
    maxValue: number | undefined,
): string => {
    const values = {
        category: category?.toUpperCase(),
        filiereResume: filiere,
        cadreEmploi,
        fonction: job !== undefined ? jobLabelFormatter(job) : undefined,
        applicationDate:
            applicationDate !== undefined
                ? formatSimulationDateString(applicationDate)
                : undefined,
        minValue,
        maxValue,
    };
    const parts: (string | JSX.Element | JSX.Element[])[] = [];

    Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            if (key === 'minValue') {
                parts.push(
                    t(
                        `humanResources.simulation.freeHypothesis.ifse.betweenMinValue`,
                        {
                            minValue: formatAmountsNumber(Number(value ?? 0)),
                        },
                    ),
                );
            } else if (key === 'maxValue') {
                parts.push(
                    t(`humanResources.simulation.freeHypothesis.ifse.and`, {
                        value: formatAmountsNumber(Number(value ?? 0)),
                    }),
                );
            } else if (key === 'applicationDate') {
                parts.push(
                    t(
                        `humanResources.simulation.freeHypothesis.ifse.applicationDateResume`,
                        {
                            date: value,
                        },
                    ),
                );
            } else if (key === 'fonction' && value) {
                parts.push(
                    t(`humanResources.simulation.freeHypothesis.ifse.andOf`, {
                        key,
                        value,
                    }),
                );
            } else {
                parts.push(
                    t(`humanResources.simulation.freeHypothesis.ifse.of`, {
                        key: keyLabelFormatter(key).toLowerCase(),
                        value,
                    }),
                );
            }
        }
    });

    return parts.join(' ');
};

/**
 * Formats the provided filters by creating a new object with only the non-empty fields.
 *
 * @param {FreeFilters} filters - the filters to be formatted
 * @return {FreeFilters} the formatted filters
 */
export const filterFilters = (filters: FreeFilters): FreeFilters => {
    const {
        cadreEmploi,
        category,
        date,
        filiere,
        greaterThan,
        jobLabel,
        lowerThan,
    } = filters;
    // Create a new object with only the non-empty fields
    const formattedFilters: FreeFilters = {
        ...(cadreEmploi && { cadreEmploi }),
        ...(category && { category }),
        ...(date && { date }),
        ...(filiere && { filiere }),
        ...(greaterThan !== undefined && { greaterThan }),
        ...(jobLabel && jobLabel.length > 0 && { jobLabel }),
        ...(lowerThan !== undefined && { lowerThan }),
    };

    return formattedFilters;
};

export const getFreeHypothesisLabel = (filters: FreeFilters): string => {
    const filteredFilters = filterFilters(filters);
    if (filteredFilters.jobLabel) {
        if (filteredFilters.jobLabel.length > 1) {
            return `${filteredFilters.jobLabel[0]} +${
                filteredFilters.jobLabel.length - 1
            }`;
        }
        return filteredFilters.jobLabel[0];
    }
    if (filteredFilters.cadreEmploi) {
        return filteredFilters.cadreEmploi;
    }
    if (filteredFilters.filiere) {
        return filteredFilters.filiere;
    }
    if (filteredFilters.category) {
        return `Catégorie ${filteredFilters.category?.toUpperCase()}`;
    }
    return '';
};
